import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Merge, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BULK = new Set(["cement", "sand", "man_dust", "aggregate_7mm", "aggregate_10mm", "aggregate_14mm", "aggregate_20mm", "exposed_rock"]);
const disp = (cat, q) =>
  BULK.has(cat)
    ? `${(Number(q || 0) / 1000).toLocaleString(undefined, { maximumFractionDigits: 2 })} t`
    : `${Number(q || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function MergeDuplicatesDialog({ open, onOpenChange, onDone }) {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [sel, setSel] = useState({});       // key -> { keepId, mergeIds:Set, mode, orgLevel }
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/inventory/duplicate-suggestions");
      const gs = r.data?.groups || [];
      setGroups(gs);
      const next = {};
      for (const g of gs) {
        next[g.key] = {
          keepId: g.suggested_keep_id,
          mergeIds: new Set(g.records.filter((x) => x.id !== g.suggested_keep_id).map((x) => x.id)),
          mode: "keep",
          orgLevel: true,
        };
      }
      setSel(next);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not scan for duplicates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const setKeep = (key, id) =>
    setSel((s) => {
      const g = groups.find((x) => x.key === key);
      const mergeIds = new Set(g.records.filter((x) => x.id !== id).map((x) => x.id));
      return { ...s, [key]: { ...s[key], keepId: id, mergeIds } };
    });
  const toggleMerge = (key, id) =>
    setSel((s) => {
      const cur = new Set(s[key].mergeIds);
      cur.has(id) ? cur.delete(id) : cur.add(id);
      return { ...s, [key]: { ...s[key], mergeIds: cur } };
    });
  const patch = (key, p) => setSel((s) => ({ ...s, [key]: { ...s[key], ...p } }));

  const doMerge = async (g) => {
    const s = sel[g.key];
    const keep = g.records.find((r) => r.id === s.keepId);
    const mergeIds = [...s.mergeIds].filter((id) => id !== s.keepId);
    if (!mergeIds.length) { toast.error("Tick at least one record to merge in"); return; }
    let final_quantity;
    if (s.mode === "sum") {
      final_quantity = Number(keep.quantity || 0) +
        g.records.filter((r) => mergeIds.includes(r.id)).reduce((a, r) => a + Number(r.quantity || 0), 0);
    }
    setBusyKey(g.key);
    try {
      const r = await api.post("/inventory/merge", {
        keep_id: s.keepId, merge_ids: mergeIds,
        final_quantity, make_org_level: s.orgLevel, confirm: "MERGE",
      });
      toast.success(`Merged ${r.data.retired_count} record${r.data.retired_count === 1 ? "" : "s"} into ${keep.sku}`);
      await load();
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Merge failed");
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-surface border-industrial max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="merge-dupes-dialog">
        <DialogHeader>
          <DialogTitle className="heading uppercase inline-flex items-center gap-2">
            <Merge className="w-5 h-5 text-primary-orange" /> Merge Duplicate Materials
          </DialogTitle>
        </DialogHeader>

        <p className="mono text-[11px] text-muted-industrial -mt-2">
          These records look like the same physical material (e.g. a plant-copy or an extra cement
          record). Pick the one to <span className="text-primary-orange">keep</span> — the others are
          retired (recoverable) and the kept record feeds both the Inventory page and the cockpit gauge.
          Distinct products are never grouped.
        </p>

        {loading ? (
          <div className="py-12 text-center mono text-sm text-muted-industrial">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Scanning stock…
          </div>
        ) : groups.length === 0 ? (
          <div className="py-12 text-center" data-testid="merge-none">
            <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <div className="mono text-sm text-ink">No duplicates found — every material has a single record.</div>
          </div>
        ) : (
          <div className="space-y-4">
            {groups.map((g) => {
              const s = sel[g.key] || {};
              const keep = g.records.find((r) => r.id === s.keepId);
              const mergeQty = g.records.filter((r) => s.mergeIds?.has(r.id) && r.id !== s.keepId)
                .reduce((a, r) => a + Number(r.quantity || 0), 0);
              const finalQty = s.mode === "sum" ? Number(keep?.quantity || 0) + mergeQty : Number(keep?.quantity || 0);
              return (
                <div key={g.key} className="card-industrial p-3" data-testid={`merge-group-${g.key}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="label-tag text-primary-orange">{g.category} · {g.records.length} records</div>
                  </div>
                  <table className="w-full text-sm mb-2">
                    <thead><tr className="text-left">
                      <th className="p-1 label-tag w-10">Keep</th>
                      <th className="p-1 label-tag w-12">Merge</th>
                      <th className="p-1 label-tag">SKU · Name</th>
                      <th className="p-1 label-tag">Silo</th>
                      <th className="p-1 label-tag text-right">On Hand</th>
                    </tr></thead>
                    <tbody>
                      {g.records.map((r) => {
                        const isKeep = r.id === s.keepId;
                        return (
                          <tr key={r.id} className={`border-t border-industrial ${isKeep ? "bg-primary-orange/5" : ""}`}>
                            <td className="p-1 text-center">
                              <input type="radio" name={`keep-${g.key}`} checked={isKeep}
                                onChange={() => setKeep(g.key, r.id)}
                                data-testid={`merge-keep-${r.sku}`} className="accent-[rgb(var(--primary))]" />
                            </td>
                            <td className="p-1 text-center">
                              {isKeep ? <span className="text-[10px] text-muted-industrial">—</span> : (
                                <input type="checkbox" checked={s.mergeIds?.has(r.id) || false}
                                  onChange={() => toggleMerge(g.key, r.id)}
                                  data-testid={`merge-pick-${r.sku}`} className="accent-[rgb(var(--primary))]" />
                              )}
                            </td>
                            <td className="p-1">
                              <span className="mono text-xs text-muted-industrial">{r.sku}</span>
                              <span className="ml-1.5">{r.name}</span>
                              {!r.plant_id && <span className="ml-1.5 text-[9px] text-muted-industrial uppercase">· plant-wide</span>}
                            </td>
                            <td className="p-1 mono text-xs">{r.silo || "—"}</td>
                            <td className="p-1 mono text-right">{disp(r.category, r.quantity)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  <div className="flex flex-wrap items-center gap-2 justify-between">
                    <div className="flex items-center gap-1">
                      {[["keep", "Keep this level"], ["sum", "Add together"]].map(([m, label]) => (
                        <button key={m} type="button" onClick={() => patch(g.key, { mode: m })}
                          data-testid={`merge-mode-${g.key}-${m}`}
                          className={`px-2 py-1 mono text-[10px] uppercase tracking-widest border ${s.mode === m ? "border-primary-orange text-primary-orange" : "border-industrial text-muted-industrial"}`}>
                          {label}
                        </button>
                      ))}
                      <label className="flex items-center gap-1 ml-2 mono text-[10px] uppercase tracking-widest text-muted-industrial cursor-pointer">
                        <input type="checkbox" checked={s.orgLevel || false}
                          onChange={(e) => patch(g.key, { orgLevel: e.target.checked })}
                          data-testid={`merge-orglevel-${g.key}`} className="accent-[rgb(var(--primary))]" />
                        Plant-wide
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="mono text-[11px] text-muted-industrial">
                        Result: <b className="text-ink">{keep?.sku}</b> @ <b className="text-primary-orange">{disp(g.category, finalQty)}</b>
                      </span>
                      <Button size="sm" onClick={() => doMerge(g)} disabled={busyKey === g.key}
                        data-testid={`merge-apply-${g.key}`}
                        className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold h-7 disabled:opacity-40">
                        {busyKey === g.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Merge"}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="flex items-start gap-2 mono text-[10px] text-muted-industrial border-t border-industrial pt-3">
              <AlertTriangle className="w-3.5 h-3.5 text-primary-orange shrink-0 mt-0.5" />
              "Keep this level" uses the kept record's current on-hand (duplicate stock discarded — use this
              when the copies are stale mirrors). "Add together" sums them. Retired records can be restored.
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
